/*
 * Passwordless email-OTP sign-in against the Drishti-only Cognito pool.
 *
 * Drishti users have no password, so sign-in uses Cognito's choice-based USER_AUTH
 * flow with an EMAIL_OTP first factor. amazon-cognito-identity-js cannot reach that
 * flow, so this module does not share FleetEdge's authenticationService.
 *
 * Transport is a plain unsigned fetch: these three operations are unauthenticated, so
 * there is nothing to sign, and an auto-retrying client would burn single-use OTP
 * attempts. Wire shape mirrors amazon-cognito-identity-js/lib/Client.js.
 */

import jwtDecode from 'jwt-decode';

import type {
    DrishtiAuthErrorCode,
    DrishtiAuthResult,
    DrishtiOtpChallenge,
    DrishtiPoolParams,
    DrishtiSignInResult,
} from './drishtiAuthTypes';

const EMAIL_OTP_CHALLENGE = 'EMAIL_OTP';

/** Pool ids are `${region}_${suffix}`; anything else would build a bogus endpoint host. */
const USER_POOL_ID_PATTERN = /^([a-z]{2}(?:-[a-z]+)+-\d)_\w+$/;

const ERROR_CODES_BY_EXCEPTION: Record<string, DrishtiAuthErrorCode> = {
    CodeMismatchException: 'CODE_MISMATCH',
    ExpiredCodeException: 'CODE_EXPIRED',
    TooManyFailedAttemptsException: 'ATTEMPT_LIMIT_EXCEEDED',
    TooManyRequestsException: 'ATTEMPT_LIMIT_EXCEEDED',
    LimitExceededException: 'ATTEMPT_LIMIT_EXCEEDED',
    // A stale or already-consumed session comes back as NotAuthorized.
    NotAuthorizedException: 'SESSION_EXPIRED',
};

const buildFailure = (code: DrishtiAuthErrorCode): DrishtiAuthResult<never> => ({ ok: false, code });

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;

const readString = (source: Record<string, unknown> | undefined, key: string): string | undefined => {
    const value = source?.[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const deriveRegion = (userPoolId: string): string | undefined => USER_POOL_ID_PATTERN.exec(userPoolId)?.[1];

/*
 * The pool is case-sensitive and preventUserExistenceErrors hides the miss, so an address
 * the technician capitalized reads as a code that never arrives rather than a sign-in error.
 * Both operations must normalize identically: RespondToAuthChallenge's USERNAME has to match
 * the one InitiateAuth resolved the session with.
 */
const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const readJsonBody = async (response: Response): Promise<Record<string, unknown> | undefined> => {
    try {
        return asRecord(await response.json());
    } catch {
        // Cognito answers some errors with an empty body; the error type is in a header instead.
        return undefined;
    }
};

const resolveExceptionName = (body: Record<string, unknown> | undefined, response: Response): string | undefined => {
    const bodyType = readString(body, '__type') ?? readString(body, 'code');
    if (bodyType !== undefined) {
        return bodyType.split('#').pop();
    }
    return response.headers.get('x-amzn-errortype')?.split(':')[0];
};

const mapException = (body: Record<string, unknown> | undefined, response: Response): DrishtiAuthErrorCode => {
    const exceptionName = resolveExceptionName(body, response) ?? '';
    // hasOwnProperty, not a bare index: an exception named `toString` would otherwise
    // return an inherited function as if it were an error code.
    return Object.prototype.hasOwnProperty.call(ERROR_CODES_BY_EXCEPTION, exceptionName)
        ? ERROR_CODES_BY_EXCEPTION[exceptionName]
        : 'UNKNOWN';
};

/** Posts one unsigned Cognito operation and hands back its parsed body. */
const callCognito = async (
    region: string,
    operation: 'InitiateAuth' | 'RespondToAuthChallenge',
    input: Record<string, unknown>,
): Promise<DrishtiAuthResult<Record<string, unknown>>> => {
    let response: Response;
    try {
        response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-amz-json-1.1',
                'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
                'Cache-Control': 'no-store',
            },
            body: JSON.stringify(input),
        });
    } catch (caught: unknown) {
        return buildFailure(caught instanceof TypeError ? 'NETWORK' : 'UNKNOWN');
    }

    const body = await readJsonBody(response);
    if (!response.ok) {
        return buildFailure(mapException(body, response));
    }
    return body === undefined ? buildFailure('UNKNOWN') : { ok: true, value: body };
};

const buildOtpChallenge = (body: Record<string, unknown>): DrishtiAuthResult<DrishtiOtpChallenge> => {
    const session = readString(body, 'Session');
    return session === undefined ? buildFailure('UNKNOWN') : { ok: true, value: { session } };
};

interface SignInResultSource {
    readonly body: Record<string, unknown>;
    readonly region: string;
    readonly userPoolId: string;
    /** Used only when the id token carries no email claim. */
    readonly email: string;
    /** REFRESH_TOKEN_AUTH returns no new refresh token, so the caller's is carried forward. */
    readonly carriedRefreshToken?: string;
}

/*
 * Memory only, never persisted: in the session slice an XSS would reach a 30-day credential
 * instead of a 12-hour id token. Accepted consequence: a reload drops it. Every sign-out clears it too,
 * so no usable credential outlives the session it belongs to.
 */
let storedRefreshToken: string | undefined;

export const clearDrishtiRefreshToken = (): void => {
    storedRefreshToken = undefined;
};

const rememberRefreshToken = (
    result: DrishtiAuthResult<DrishtiSignInResult>,
): DrishtiAuthResult<DrishtiSignInResult> => {
    if (result.ok) {
        storedRefreshToken = result.value.refreshToken;
    }
    return result;
};

const decodeIdTokenClaims = (idToken: string): Record<string, unknown> | undefined => {
    try {
        return asRecord(jwtDecode(idToken));
    } catch {
        // An undecodable token is as unusable as a malformed body, and maps the same way.
        return undefined;
    }
};

/*
 * The request names only the client id, so a config pairing the Drishti pool id with another pool's
 * client would mint a foreign token and label it Drishti. The issuer claim is the only proof of origin,
 * and the whole issuer is compared: matching on the pool id alone would accept any host that ends in it.
 */
const isTokenFromPool = (claims: Record<string, unknown> | undefined, region: string, userPoolId: string): boolean =>
    readString(claims, 'iss') === `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;

const buildSignInResult = (source: SignInResultSource): DrishtiAuthResult<DrishtiSignInResult> => {
    const authenticationResult = asRecord(source.body.AuthenticationResult);
    const jwt = readString(authenticationResult, 'IdToken');
    const refreshToken = readString(authenticationResult, 'RefreshToken') ?? source.carriedRefreshToken;
    const claims = jwt === undefined ? undefined : decodeIdTokenClaims(jwt);
    const expire = claims?.exp;
    // Fills the shared UserSession shape; a Drishti event's userId is stamped from the token by API Gateway.
    const username = readString(claims, 'cognito:username');
    if (jwt === undefined || refreshToken === undefined || username === undefined || typeof expire !== 'number') {
        return buildFailure('UNKNOWN');
    }
    if (!isTokenFromPool(claims, source.region, source.userPoolId)) {
        return buildFailure('UNKNOWN');
    }
    return {
        ok: true,
        value: {
            jwt,
            refreshToken,
            expire,
            email: readString(claims, 'email') ?? source.email,
            username,
            poolId: source.userPoolId,
            givenName: readString(claims, 'given_name'),
            familyName: readString(claims, 'family_name'),
            company: readString(claims, 'custom:company'),
        },
    };
};

/** Step 1: ask Cognito to email a one-time code. */
export async function startEmailOtpSignIn(
    params: DrishtiPoolParams & { readonly email: string },
): Promise<DrishtiAuthResult<DrishtiOtpChallenge>> {
    const region = deriveRegion(params.userPoolId);
    if (region === undefined) {
        return buildFailure('UNKNOWN');
    }
    // EMAIL_OTP is the pool's only allowed first factor, so PREFERRED_CHALLENGE always
    // resolves and Cognito never falls back to SELECT_CHALLENGE.
    const initiated = await callCognito(region, 'InitiateAuth', {
        AuthFlow: 'USER_AUTH',
        ClientId: params.clientId,
        AuthParameters: { USERNAME: normalizeEmail(params.email), PREFERRED_CHALLENGE: EMAIL_OTP_CHALLENGE },
    });
    if (!initiated.ok) {
        return initiated;
    }
    if (readString(initiated.value, 'ChallengeName') !== EMAIL_OTP_CHALLENGE) {
        return buildFailure('UNKNOWN');
    }
    return buildOtpChallenge(initiated.value);
}

/** Step 2: answer the EMAIL_OTP challenge with the code the technician received. */
export async function confirmEmailOtp(
    params: DrishtiPoolParams & { readonly email: string; readonly code: string; readonly session: string },
): Promise<DrishtiAuthResult<DrishtiSignInResult>> {
    const region = deriveRegion(params.userPoolId);
    if (region === undefined) {
        return buildFailure('UNKNOWN');
    }
    const email = normalizeEmail(params.email);
    const confirmed = await callCognito(region, 'RespondToAuthChallenge', {
        ChallengeName: EMAIL_OTP_CHALLENGE,
        ClientId: params.clientId,
        Session: params.session,
        // Trimmed for the same reason retries are absent: a stray space would burn a single-use attempt.
        ChallengeResponses: { USERNAME: email, EMAIL_OTP_CODE: params.code.trim() },
    });
    if (!confirmed.ok) {
        return confirmed;
    }
    return rememberRefreshToken(
        buildSignInResult({ body: confirmed.value, region, userPoolId: params.userPoolId, email }),
    );
}

/** Re-mints an expired id token. Replaces CognitoRefreshToken, which cannot see this pool's session. */
export async function refreshDrishtiSession(
    params: DrishtiPoolParams & { readonly email: string },
): Promise<DrishtiAuthResult<DrishtiSignInResult>> {
    const refreshToken = storedRefreshToken;
    if (refreshToken === undefined) {
        return buildFailure('SESSION_EXPIRED');
    }
    const region = deriveRegion(params.userPoolId);
    if (region === undefined) {
        return buildFailure('UNKNOWN');
    }
    const refreshed = await callCognito(region, 'InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: params.clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
    });
    if (!refreshed.ok) {
        // Only a rejected credential is dead. A network blip keeps the token so the next attempt can retry it.
        if (refreshed.code === 'SESSION_EXPIRED') {
            clearDrishtiRefreshToken();
        }
        return refreshed;
    }
    return rememberRefreshToken(
        buildSignInResult({
            body: refreshed.value,
            region,
            userPoolId: params.userPoolId,
            email: params.email,
            carriedRefreshToken: refreshToken,
        }),
    );
}
